import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Zap, Trophy, Crown, Medal, Award, Target, Activity,
  TrendingUp, BarChart3, RefreshCw, ExternalLink, Users
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RANK_STYLES = {
  1: { bg: 'bg-amber-500/20', border: 'border-amber-500/50', text: 'text-amber-400', icon: Crown },
  2: { bg: 'bg-slate-300/10', border: 'border-slate-400/50', text: 'text-slate-300', icon: Medal },
  3: { bg: 'bg-orange-600/15', border: 'border-orange-600/50', text: 'text-orange-400', icon: Award },
};

export default function PublicLeaderboard() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/public/leaderboard`);
      setData(res.data);
    } catch (err) {
      console.error('Error fetching public leaderboard:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030508] flex items-center justify-center">
        <div className="text-center">
          <Trophy className="w-14 h-14 text-amber-500 animate-pulse mx-auto mb-4" />
          <p className="text-slate-400 font-mono text-sm">{t('leaderboard.loading')}</p>
        </div>
      </div>
    );
  }

  const leaderboard = data?.leaderboard || [];
  const chartData = leaderboard.slice(0, 10).map(a => ({
    name: a.name?.split(' ').slice(0, 2).join(' '),
    alpha: a.alpha_score,
    return: a.total_return
  }));

  return (
    <div className="min-h-screen bg-[#030508]" data-testid="public-leaderboard">
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-white flex items-center justify-center">
                  <Zap className="w-6 h-6 text-[#030508]" />
                </div>
                <div>
                  <h1 className="text-2xl font-black text-white" style={{ fontFamily: 'Chivo' }}>
                    BlackOre<span className="text-emerald-500">X</span>
                  </h1>
                  <p className="text-xs text-slate-500 font-mono uppercase tracking-wider">{t('leaderboard.title')}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right mr-4 hidden md:block">
                <p className="text-xs text-slate-500">{t('leaderboard.liveAgents')}</p>
                <p className="text-lg font-bold text-emerald-400 font-mono">{data?.live_agents || 0}/{data?.total_agents || 0}</p>
              </div>
              <Link to="/login"
                className="flex items-center gap-2 px-4 py-2 bg-white text-[#030508] font-bold text-sm hover:bg-slate-200 transition-colors"
                data-testid="login-cta">
                <ExternalLink className="w-4 h-4" />
                ENTER
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto p-4 md:p-6 space-y-6">
        {/* Platform Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-[#090B0F] border border-white/10 p-4 text-center">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">{t('leaderboard.aiAgents')}</p>
            <p className="text-3xl font-black text-white font-mono">{data?.total_agents || 0}</p>
          </div>
          <div className="bg-[#090B0F] border border-white/10 p-4 text-center">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">{t('leaderboard.markets')}</p>
            <p className="text-3xl font-black text-emerald-400 font-mono">{data?.markets?.length || 0}</p>
          </div>
          <div className="bg-[#090B0F] border border-white/10 p-4 text-center">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">{t('leaderboard.cycles')}</p>
            <p className="text-3xl font-black text-white font-mono">{data?.total_cycles?.toLocaleString() || 0}</p>
          </div>
          <div className="bg-[#090B0F] border border-white/10 p-4 text-center">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">{t('leaderboard.trades')}</p>
            <p className="text-3xl font-black text-white font-mono">{data?.total_trades?.toLocaleString() || 0}</p>
          </div>
        </div>

        {/* Markets Covered */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-slate-500 font-mono uppercase tracking-wider mr-2">Markets:</span>
          {(data?.markets || []).map(m => (
            <span key={m} className="px-3 py-1 bg-emerald-500/10 text-emerald-400 text-xs font-mono border border-emerald-500/20">
              {m}
            </span>
          ))}
        </div>

        {/* Alpha Chart */}
        <div className="bg-[#090B0F] border border-white/10 p-4 md:p-6">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-5 h-5 text-amber-500" />
            <h2 className="text-sm uppercase tracking-[0.2em] font-semibold text-white">{t('leaderboard.alphaByAgent')}</h2>
          </div>
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 9 }} angle={-15} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 10 }} />
                <Tooltip contentStyle={{ backgroundColor: '#11141D', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 0 }} />
                <Bar dataKey="alpha" radius={[2, 2, 0, 0]}>
                  {chartData.map((entry, i) => (
                    <Cell key={i} fill={entry.alpha >= 0 ? '#10B981' : '#EF4444'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Leaderboard Table */}
        <div className="bg-[#090B0F] border border-white/10 p-4 md:p-6" data-testid="public-leaderboard-table">
          <div className="flex items-center gap-2 mb-6">
            <Trophy className="w-5 h-5 text-amber-500" />
            <h2 className="text-sm uppercase tracking-[0.2em] font-semibold text-white">{t('leaderboard.agentRanking')}</h2>
            <span className="text-xs text-slate-500 font-mono ml-auto">
              Updated: {data?.last_updated ? new Date(data.last_updated).toLocaleTimeString() : '-'}
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-3 px-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">#</th>
                  <th className="text-left py-3 px-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">{t('common.agent')}</th>
                  <th className="text-left py-3 px-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">{t('common.type')}</th>
                  <th className="text-left py-3 px-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">{t('common.market')}</th>
                  <th className="text-right py-3 px-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">{t('common.alpha')}</th>
                  <th className="text-right py-3 px-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">{t('common.return')}</th>
                  <th className="text-right py-3 px-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">{t('common.sharpe')}</th>
                  <th className="text-right py-3 px-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">{t('common.winRate')}</th>
                  <th className="text-center py-3 px-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">{t('common.status')}</th>
                </tr>
              </thead>
              <tbody>
                {leaderboard.map(agent => {
                  const rs = RANK_STYLES[agent.rank];
                  const RIcon = rs?.icon || Target;
                  return (
                    <tr key={agent.rank} className={`border-b border-white/5 hover:bg-[#11141D] transition-colors ${rs ? rs.bg : ''}`}
                      data-testid={`public-agent-row-${agent.rank}`}>
                      <td className="py-3 px-2">
                        {agent.rank <= 3 ? (
                          <RIcon className={`w-5 h-5 ${rs?.text || 'text-slate-400'}`} />
                        ) : (
                          <span className="font-mono text-slate-500">#{agent.rank}</span>
                        )}
                      </td>
                      <td className="py-3 px-2">
                        <span className={`font-bold text-sm ${rs ? rs.text : 'text-white'}`}>{agent.name}</span>
                      </td>
                      <td className="py-3 px-2 font-mono text-xs text-slate-400">{agent.type}</td>
                      <td className="py-3 px-2 text-xs text-slate-400">{agent.market}</td>
                      <td className={`py-3 px-2 text-right font-mono font-bold ${agent.alpha_score >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {agent.alpha_score >= 0 ? '+' : ''}{agent.alpha_score}
                      </td>
                      <td className={`py-3 px-2 text-right font-mono ${agent.total_return >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {agent.total_return >= 0 ? '+' : ''}{agent.total_return}%
                      </td>
                      <td className="py-3 px-2 text-right font-mono text-white">{agent.sharpe_ratio}</td>
                      <td className="py-3 px-2 text-right font-mono text-white">{agent.win_rate}%</td>
                      <td className="py-3 px-2 text-center">
                        <span className={`inline-block w-2 h-2 rounded-full ${agent.status === 'LIVE' ? 'bg-emerald-500 animate-pulse' : 'bg-slate-600'}`} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer CTA */}
        <div className="text-center py-8 border-t border-white/5">
          <p className="text-slate-500 text-sm mb-4 font-mono">{t('leaderboard.footer')}</p>
          <Link to="/login"
            className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-500 text-black font-bold text-sm hover:bg-emerald-400 transition-colors"
            data-testid="footer-login-cta">
            <Zap className="w-4 h-4" />
            COMENZAR A OPERAR
          </Link>
        </div>
      </main>
    </div>
  );
}
